import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import ScienceIcon from "@mui/icons-material/Science";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import AssessmentIcon from "@mui/icons-material/Assessment";

const ClinicalCalculatorsPage = () => {
  const [activeCalculator, setActiveCalculator] = useState("mayo");

  // Mayo Staging System
  const [mayoInputs, setMayoInputs] = useState({
    ntProBNP: "",
    troponin: "",
    dFLC: "",
  });
  const [mayoResult, setMayoResult] = useState(null);

  // Boston Criteria
  const [bostonInputs, setBostonInputs] = useState({
    ttecho: "",
    ecgLowVoltage: "",
    pseudoInfarct: "",
    lvh: "",
  });
  const [bostonResult, setBostonResult] = useState(null);

  // eGFR Calculator
  const [egfrInputs, setEgfrInputs] = useState({
    creatinine: "",
    age: "",
    gender: "male",
    race: "other",
  });
  const [egfrResult, setEgfrResult] = useState(null);

  // Free Light Chain Ratio
  const [flcInputs, setFlcInputs] = useState({
    kappa: "",
    lambda: "",
  });
  const [flcResult, setFlcResult] = useState(null);

  // NYHA Class
  const [nyhaClass, setNyhaClass] = useState("");

  const calculators = [
    {
      id: "mayo",
      name: "Mayo Staging System (AL)",
      icon: <MonitorHeartIcon />,
    },
    {
      id: "boston",
      name: "Boston Criteria (Cardiac Amyloid)",
      icon: <AssessmentIcon />,
    },
    { id: "egfr", name: "eGFR Calculator", icon: <ScienceIcon /> },
    { id: "flc", name: "Free Light Chain Ratio", icon: <ScienceIcon /> },
    { id: "nyha", name: "NYHA Functional Class", icon: <MonitorHeartIcon /> },
  ];

  // Mayo Staging Calculation
  const calculateMayoStage = () => {
    const ntProBNP = parseFloat(mayoInputs.ntProBNP);
    const troponin = parseFloat(mayoInputs.troponin);
    const dFLC = parseFloat(mayoInputs.dFLC);

    if (isNaN(ntProBNP) || isNaN(troponin) || isNaN(dFLC)) {
      alert("Please enter all values");
      return;
    }

    let points = 0;
    if (ntProBNP >= 1800) points++;
    if (troponin >= 0.025) points++;
    if (dFLC >= 18) points++;

    let stage = "";
    let medianSurvival = "";
    let riskCategory = "";

    if (points === 0) {
      stage = "Mayo Stage I";
      medianSurvival = "94 months";
      riskCategory = "Low Risk";
    } else if (points === 1) {
      stage = "Mayo Stage II";
      medianSurvival = "40 months";
      riskCategory = "Intermediate Risk";
    } else if (points === 2) {
      stage = "Mayo Stage III";
      medianSurvival = "14 months";
      riskCategory = "High Risk";
    } else {
      stage = "Mayo Stage IV";
      medianSurvival = "6 months";
      riskCategory = "Very High Risk";
    }

    setMayoResult({
      stage,
      points,
      medianSurvival,
      riskCategory,
      biomarkers: {
        ntProBNP:
          ntProBNP >= 1800 ? "Elevated (≥1800 pg/mL)" : "Normal (<1800 pg/mL)",
        troponin:
          troponin >= 0.025
            ? "Elevated (≥0.025 ng/mL)"
            : "Normal (<0.025 ng/mL)",
        dFLC: dFLC >= 18 ? "Elevated (≥18 mg/dL)" : "Normal (<18 mg/dL)",
      },
    });
  };

  // Boston Criteria Calculation
  const calculateBoston = () => {
    const { ttecho, ecgLowVoltage, pseudoInfarct, lvh } = bostonInputs;

    if (!ttecho || !ecgLowVoltage || !pseudoInfarct || !lvh) {
      alert("Please answer all questions");
      return;
    }

    let score = 0;

    // Echocardiogram
    if (ttecho === ">12mm") score += 2;

    // ECG findings
    if (ecgLowVoltage === "yes") score += 1;
    if (pseudoInfarct === "yes") score += 1;

    // No LVH on ECG
    if (lvh === "no" && ttecho === ">12mm") score += 1;

    let interpretation = "";
    let probability = "";

    if (score === 0) {
      interpretation = "Low probability of cardiac amyloidosis";
      probability = "<5%";
    } else if (score === 1) {
      interpretation = "Low-intermediate probability";
      probability = "5-15%";
    } else if (score === 2) {
      interpretation = "Intermediate probability";
      probability = "15-40%";
    } else if (score === 3) {
      interpretation = "Intermediate-high probability";
      probability = "40-70%";
    } else {
      interpretation = "High probability of cardiac amyloidosis";
      probability = ">70%";
    }

    setBostonResult({
      score,
      interpretation,
      probability,
      recommendation:
        score >= 2
          ? "Further workup recommended (cardiac MRI, nuclear imaging, or biopsy)"
          : "Consider alternative diagnoses if clinical suspicion remains high",
    });
  };

  // eGFR Calculation (CKD-EPI)
  const calculateEGFR = () => {
    const creatinine = parseFloat(egfrInputs.creatinine);
    const age = parseInt(egfrInputs.age);

    if (isNaN(creatinine) || isNaN(age)) {
      alert("Please enter creatinine and age");
      return;
    }

    const isFemale = egfrInputs.gender === "female";
    const isBlack = egfrInputs.race === "black";

    let kappa = isFemale ? 0.7 : 0.9;
    let alpha = isFemale ? -0.329 : -0.411;
    let min = Math.min(creatinine / kappa, 1);
    let max = Math.max(creatinine / kappa, 1);

    let egfr =
      141 * Math.pow(min, alpha) * Math.pow(max, -1.209) * Math.pow(0.993, age);

    if (isFemale) egfr *= 1.018;
    if (isBlack) egfr *= 1.159;

    egfr = Math.round(egfr);

    let stage = "";
    let description = "";

    if (egfr >= 90) {
      stage = "G1 - Normal or high";
      description = "Normal kidney function";
    } else if (egfr >= 60) {
      stage = "G2 - Mildly decreased";
      description = "Mildly decreased kidney function";
    } else if (egfr >= 45) {
      stage = "G3a - Mild to moderate";
      description = "Mild to moderately decreased";
    } else if (egfr >= 30) {
      stage = "G3b - Moderate to severe";
      description = "Moderately to severely decreased";
    } else if (egfr >= 15) {
      stage = "G4 - Severely decreased";
      description = "Severely decreased kidney function";
    } else {
      stage = "G5 - Kidney failure";
      description = "Kidney failure (consider dialysis)";
    }

    setEgfrResult({
      egfr,
      stage,
      description,
      warning:
        egfr < 60
          ? "Consider nephrology referral for eGFR <60 mL/min/1.73m²"
          : null,
    });
  };

  // Free Light Chain Ratio
  const calculateFLC = () => {
    const kappa = parseFloat(flcInputs.kappa);
    const lambda = parseFloat(flcInputs.lambda);

    if (isNaN(kappa) || isNaN(lambda) || lambda === 0) {
      alert("Please enter valid kappa and lambda values");
      return;
    }

    const ratio = kappa / lambda;
    const dFLC = Math.abs(kappa - lambda);

    let interpretation = "";
    let concern = "";

    if (ratio >= 0.26 && ratio <= 1.65) {
      interpretation = "Normal ratio";
      concern = "No monoclonal protein detected by this test";
    } else if (ratio > 1.65) {
      interpretation = "Kappa-predominant (ratio elevated)";
      concern = "Suggests kappa light chain monoclonal protein";
    } else {
      interpretation = "Lambda-predominant (ratio decreased)";
      concern = "Suggests lambda light chain monoclonal protein";
    }

    setFlcResult({
      kappa: kappa.toFixed(2),
      lambda: lambda.toFixed(2),
      ratio: ratio.toFixed(2),
      dFLC: dFLC.toFixed(2),
      interpretation,
      concern,
      normalRange: "0.26 - 1.65",
      isAbnormal: ratio < 0.26 || ratio > 1.65,
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <CalculateIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Clinical Calculators
          </Typography>
          <Chip label="Free" color="success" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Essential calculators for amyloidosis assessment and management
        </Typography>
      </Box>

      {/* Important Notice */}
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>For Healthcare Professionals</strong>
        </Typography>
        <Typography variant="body2">
          These calculators are clinical decision support tools. Results should
          be interpreted in the context of the full clinical picture. Not
          intended for self-diagnosis.
        </Typography>
      </Alert>

      {/* Calculator Selection - Horizontal Card Buttons */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: "1rem",
            "&::after": {
              content: '""',
              flex: 1,
              height: "2px",
              background:
                "linear-gradient(90deg, #1A5EA8 0%, transparent 100%)",
              ml: 2,
            },
          }}
        >
          <CalculateIcon sx={{ color: "#1A5EA8", fontSize: 22 }} />
          Select Calculator
        </Typography>
        <Grid container spacing={1.5}>
          {calculators.map((calc) => (
            <Grid item xs={6} sm={4} md={2.4} key={calc.id}>
              <Card
                onClick={() => setActiveCalculator(calc.id)}
                sx={{
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  border: "none",
                  borderRadius: 3,
                  background:
                    activeCalculator === calc.id
                      ? "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)"
                      : "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  height: "100%",
                  minHeight: 110,
                  boxShadow:
                    activeCalculator === calc.id
                      ? "0 6px 20px rgba(102, 126, 234, 0.3)"
                      : "0 2px 8px rgba(0, 0, 0, 0.08)",
                  "&:hover": {
                    transform: "translateY(-6px) scale(1.02)",
                    boxShadow:
                      activeCalculator === calc.id
                        ? "0 10px 28px rgba(102, 126, 234, 0.4)"
                        : "0 6px 20px rgba(102, 126, 234, 0.2)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    borderRadius: "12px 12px 0 0",
                    background:
                      activeCalculator === calc.id
                        ? "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)"
                        : "transparent",
                    transition: "all 0.3s",
                  },
                }}
              >
                <CardContent
                  sx={{
                    textAlign: "center",
                    py: 2,
                    px: 1.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    zIndex: 1,
                    "&:last-child": { pb: 2 },
                  }}
                >
                  {/* Icon Container with Circular Background */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                      background:
                        activeCalculator === calc.id
                          ? "rgba(255, 255, 255, 0.25)"
                          : "rgba(102, 126, 234, 0.08)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s",
                      boxShadow:
                        activeCalculator === calc.id
                          ? "0 3px 10px rgba(255, 255, 255, 0.2)"
                          : "0 2px 6px rgba(102, 126, 234, 0.1)",
                    }}
                  >
                    {React.cloneElement(calc.icon, {
                      sx: {
                        fontSize: 28,
                        color:
                          activeCalculator === calc.id ? "white" : "#1A5EA8",
                        filter:
                          activeCalculator === calc.id
                            ? "drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
                            : "none",
                        transition: "all 0.3s",
                      },
                    })}
                  </Box>

                  {/* Calculator Name */}
                  <Typography
                    variant="body2"
                    fontWeight={activeCalculator === calc.id ? 700 : 600}
                    sx={{
                      fontSize: "0.8rem",
                      lineHeight: 1.3,
                      color: activeCalculator === calc.id ? "white" : "#2c3e50",
                      textShadow:
                        activeCalculator === calc.id
                          ? "0 1px 2px rgba(0,0,0,0.1)"
                          : "none",
                      transition: "all 0.3s",
                      letterSpacing: "0.2px",
                    }}
                  >
                    {calc.name}
                  </Typography>

                  {/* Active Indicator Badge */}
                  {activeCalculator === calc.id && (
                    <Chip
                      label="ACTIVE"
                      size="small"
                      sx={{
                        mt: 1,
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        bgcolor: "rgba(255, 255, 255, 0.95)",
                        color: "#1A5EA8",
                        letterSpacing: "0.5px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    />
                  )}
                </CardContent>

                {/* Decorative Corner Accent */}
                {activeCalculator === calc.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -15,
                      right: -15,
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      background:
                        "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Calculator Content */}
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Mayo Staging System */}
        {activeCalculator === "mayo" && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Mayo Staging System for AL Amyloidosis
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Risk stratification based on cardiac biomarkers and free light
              chains
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                The Mayo Staging System uses NT-proBNP, troponin, and difference
                in free light chains (dFLC) to predict survival in AL
                amyloidosis.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="NT-proBNP"
                  type="number"
                  value={mayoInputs.ntProBNP}
                  onChange={(e) =>
                    setMayoInputs({ ...mayoInputs, ntProBNP: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">pg/mL</InputAdornment>
                    ),
                  }}
                  helperText="Normal: <125 pg/mL"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Troponin T"
                  type="number"
                  value={mayoInputs.troponin}
                  onChange={(e) =>
                    setMayoInputs({ ...mayoInputs, troponin: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">ng/mL</InputAdornment>
                    ),
                  }}
                  helperText="Normal: <0.014 ng/mL"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="dFLC (Difference)"
                  type="number"
                  value={mayoInputs.dFLC}
                  onChange={(e) =>
                    setMayoInputs({ ...mayoInputs, dFLC: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mg/dL</InputAdornment>
                    ),
                  }}
                  helperText="|Kappa - Lambda|"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={calculateMayoStage}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                    py: 1.5,
                  }}
                >
                  Calculate Mayo Stage
                </Button>
              </Grid>
            </Grid>

            {mayoResult && (
              <Box sx={{ mt: 4 }}>
                <Card
                  elevation={2}
                  sx={{ bgcolor: "rgba(102, 126, 234, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color="primary.main"
                      gutterBottom
                    >
                      {mayoResult.stage}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {mayoResult.riskCategory}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Risk Score:</strong> {mayoResult.points}{" "}
                          points
                        </Typography>
                        <Typography variant="subtitle2">
                          <strong>Median Survival:</strong>{" "}
                          {mayoResult.medianSurvival}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Biomarker Status:</strong>
                        </Typography>
                        <Typography variant="caption" display="block">
                          NT-proBNP: {mayoResult.biomarkers.ntProBNP}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Troponin: {mayoResult.biomarkers.troponin}
                        </Typography>
                        <Typography variant="caption" display="block">
                          dFLC: {mayoResult.biomarkers.dFLC}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> This staging system predicts overall
                    survival but should be used alongside other clinical factors
                    in treatment decisions.
                  </Typography>
                </Alert>
              </Box>
            )}

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Mayo Staging Reference
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Stage</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Criteria</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Median Survival</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Stage I</TableCell>
                      <TableCell>0 biomarkers elevated</TableCell>
                      <TableCell>94 months</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Stage II</TableCell>
                      <TableCell>1 biomarker elevated</TableCell>
                      <TableCell>40 months</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Stage III</TableCell>
                      <TableCell>2 biomarkers elevated</TableCell>
                      <TableCell>14 months</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Stage IV</TableCell>
                      <TableCell>All 3 biomarkers elevated</TableCell>
                      <TableCell>6 months</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Cutoffs: NT-proBNP ≥1800 pg/mL, Troponin T ≥0.025 ng/mL, dFLC
                ≥18 mg/dL
              </Typography>
            </Box>
          </Box>
        )}

        {/* Boston Criteria */}
        {activeCalculator === "boston" && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Boston Criteria for Cardiac Amyloidosis
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Screening tool to assess probability of cardiac amyloidosis
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                The Boston Criteria uses echocardiographic and ECG findings to
                estimate the probability of cardiac amyloidosis.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Interventricular septal thickness on echo:
                  </FormLabel>
                  <RadioGroup
                    value={bostonInputs.ttecho}
                    onChange={(e) =>
                      setBostonInputs({
                        ...bostonInputs,
                        ttecho: e.target.value,
                      })
                    }
                  >
                    <FormControlLabel
                      value="≤12mm"
                      control={<Radio />}
                      label="≤12 mm (0 points)"
                    />
                    <FormControlLabel
                      value=">12mm"
                      control={<Radio />}
                      label=">12 mm (2 points)"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Low voltage on ECG (limb leads &lt 0.5 mV):
                  </FormLabel>
                  <RadioGroup
                    value={bostonInputs.ecgLowVoltage}
                    onChange={(e) =>
                      setBostonInputs({
                        ...bostonInputs,
                        ecgLowVoltage: e.target.value,
                      })
                    }
                  >
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No (0 points)"
                    />
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes (1 point)"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Pseudoinfarct pattern on ECG:
                  </FormLabel>
                  <RadioGroup
                    value={bostonInputs.pseudoInfarct}
                    onChange={(e) =>
                      setBostonInputs({
                        ...bostonInputs,
                        pseudoInfarct: e.target.value,
                      })
                    }
                  >
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No (0 points)"
                    />
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes (1 point)"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Left ventricular hypertrophy (LVH) on ECG:
                  </FormLabel>
                  <RadioGroup
                    value={bostonInputs.lvh}
                    onChange={(e) =>
                      setBostonInputs({ ...bostonInputs, lvh: e.target.value })
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label="Yes (0 points)"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label="No - adds 1 point if wall thickness >12mm"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={calculateBoston}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                    py: 1.5,
                  }}
                >
                  Calculate Score
                </Button>
              </Grid>
            </Grid>

            {bostonResult && (
              <Box sx={{ mt: 4 }}>
                <Card
                  elevation={2}
                  sx={{ bgcolor: "rgba(102, 126, 234, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color="primary.main"
                      gutterBottom
                    >
                      Score: {bostonResult.score} points
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {bostonResult.interpretation}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Estimated Probability:</strong>{" "}
                      {bostonResult.probability}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      <strong>Recommendation:</strong>{" "}
                      {bostonResult.recommendation}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        )}

        {/* eGFR Calculator */}
        {activeCalculator === "egfr" && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              eGFR Calculator (CKD-EPI)
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Estimate kidney function using the CKD-EPI equation
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Serum Creatinine"
                  type="number"
                  value={egfrInputs.creatinine}
                  onChange={(e) =>
                    setEgfrInputs({ ...egfrInputs, creatinine: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mg/dL</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Age"
                  type="number"
                  value={egfrInputs.age}
                  onChange={(e) =>
                    setEgfrInputs({ ...egfrInputs, age: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">years</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={egfrInputs.gender}
                    label="Gender"
                    onChange={(e) =>
                      setEgfrInputs({ ...egfrInputs, gender: e.target.value })
                    }
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Race</InputLabel>
                  <Select
                    value={egfrInputs.race}
                    label="Race"
                    onChange={(e) =>
                      setEgfrInputs({ ...egfrInputs, race: e.target.value })
                    }
                  >
                    <MenuItem value="black">Black/African American</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={calculateEGFR}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                    py: 1.5,
                  }}
                >
                  Calculate eGFR
                </Button>
              </Grid>
            </Grid>

            {egfrResult && (
              <Box sx={{ mt: 4 }}>
                <Card
                  elevation={2}
                  sx={{ bgcolor: "rgba(102, 126, 234, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color="primary.main"
                      gutterBottom
                    >
                      {egfrResult.egfr} mL/min/1.73m²
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {egfrResult.stage}
                    </Typography>
                    <Typography variant="body2">
                      {egfrResult.description}
                    </Typography>

                    {egfrResult.warning && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        {egfrResult.warning}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                CKD Stages
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Stage</strong>
                      </TableCell>
                      <TableCell>
                        <strong>eGFR (mL/min/1.73m²)</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Description</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>G1</TableCell>
                      <TableCell>≥90</TableCell>
                      <TableCell>Normal or high</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>G2</TableCell>
                      <TableCell>60-89</TableCell>
                      <TableCell>Mildly decreased</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>G3a</TableCell>
                      <TableCell>45-59</TableCell>
                      <TableCell>Mild to moderate</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>G3b</TableCell>
                      <TableCell>30-44</TableCell>
                      <TableCell>Moderate to severe</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>G4</TableCell>
                      <TableCell>15-29</TableCell>
                      <TableCell>Severely decreased</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>G5</TableCell>
                      <TableCell>&lt;15</TableCell>
                      <TableCell>Kidney failure</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}

        {/* Free Light Chain Ratio */}
        {activeCalculator === "flc" && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Free Light Chain Ratio Calculator
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Calculate kappa/lambda ratio for AL amyloidosis screening
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Abnormal free light chain ratios suggest monoclonal protein
                production and are key to AL amyloidosis diagnosis and
                monitoring.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Free Kappa"
                  type="number"
                  value={flcInputs.kappa}
                  onChange={(e) =>
                    setFlcInputs({ ...flcInputs, kappa: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mg/L</InputAdornment>
                    ),
                  }}
                  helperText="Normal: 3.3-19.4 mg/L"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Free Lambda"
                  type="number"
                  value={flcInputs.lambda}
                  onChange={(e) =>
                    setFlcInputs({ ...flcInputs, lambda: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mg/L</InputAdornment>
                    ),
                  }}
                  helperText="Normal: 5.7-26.3 mg/L"
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={calculateFLC}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                    py: 1.5,
                  }}
                >
                  Calculate Ratio
                </Button>
              </Grid>
            </Grid>

            {flcResult && (
              <Box sx={{ mt: 4 }}>
                <Card
                  elevation={2}
                  sx={{
                    bgcolor: flcResult.isAbnormal
                      ? "rgba(244, 67, 54, 0.05)"
                      : "rgba(76, 175, 80, 0.05)",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color={
                        flcResult.isAbnormal ? "error.main" : "success.main"
                      }
                      gutterBottom
                    >
                      Ratio: {flcResult.ratio}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {flcResult.interpretation}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">
                          <strong>Kappa:</strong> {flcResult.kappa} mg/L
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">
                          <strong>Lambda:</strong> {flcResult.lambda} mg/L
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">
                          <strong>dFLC:</strong> {flcResult.dFLC} mg/L
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2">
                          <strong>Normal Ratio:</strong> {flcResult.normalRange}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Alert
                      severity={flcResult.isAbnormal ? "warning" : "success"}
                      sx={{ mt: 2 }}
                    >
                      <Typography variant="body2">
                        {flcResult.concern}
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        )}

        {/* NYHA Classification */}
        {activeCalculator === "nyha" && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              NYHA Functional Classification
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              New York Heart Association functional classification for heart
              failure
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                The NYHA classification is used to assess the severity of heart
                failure symptoms and guide treatment decisions in cardiac
                amyloidosis.
              </Typography>
            </Alert>

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 2 }}>
                Select the class that best describes the patient's symptoms:
              </FormLabel>
              <RadioGroup
                value={nyhaClass}
                onChange={(e) => setNyhaClass(e.target.value)}
              >
                <Card
                  sx={{
                    mb: 2,
                    border:
                      nyhaClass === "I"
                        ? "2px solid #1A5EA8"
                        : "1px solid #e0e0e0",
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      value="I"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Class I - No Limitation
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No symptoms with ordinary physical activity. No
                            limitation of physical activity.
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    mb: 2,
                    border:
                      nyhaClass === "II"
                        ? "2px solid #1A5EA8"
                        : "1px solid #e0e0e0",
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      value="II"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Class II - Slight Limitation
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Mild symptoms (mild shortness of breath, fatigue)
                            with ordinary activity. Comfortable at rest.
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    mb: 2,
                    border:
                      nyhaClass === "III"
                        ? "2px solid #1A5EA8"
                        : "1px solid #e0e0e0",
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      value="III"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Class III - Marked Limitation
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Marked limitation. Symptoms with less than ordinary
                            activity. Comfortable only at rest.
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    mb: 2,
                    border:
                      nyhaClass === "IV"
                        ? "2px solid #1A5EA8"
                        : "1px solid #e0e0e0",
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      value="IV"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            Class IV - Severe Limitation
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Unable to carry out any physical activity without
                            discomfort. Symptoms present even at rest.
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </RadioGroup>
            </FormControl>

            {nyhaClass && (
              <Box sx={{ mt: 4 }}>
                <Alert severity="info">
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Clinical Implications:</strong>
                  </Typography>
                  <Typography variant="body2">
                    {nyhaClass === "I" &&
                      "Patients are generally eligible for most clinical trials. Continue current therapy and monitor."}
                    {nyhaClass === "II" &&
                      "Consider optimization of medical therapy. May qualify for clinical trials."}
                    {nyhaClass === "III" &&
                      "Requires aggressive medical management. Consider advanced therapies or clinical trials. May need closer monitoring."}
                    {nyhaClass === "IV" &&
                      "Severe symptoms requiring advanced heart failure management. Consider transplant evaluation, mechanical support, or palliative care."}
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Disclaimer:</strong> These calculators are educational tools
          for healthcare professionals. Results should not replace clinical
          judgment or comprehensive patient assessment. Always verify
          calculations and interpret results in the context of the complete
          clinical picture.
        </Typography>
      </Paper>
    </Container>
  );
};

export default ClinicalCalculatorsPage;
